'use client';

import type { Row, Table } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import Link from 'next/link';

import { AvatarSkeleton, TextSkeleton } from '@/components/Skeleton';

import { HYPERCORE_CHAIN_ID, NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';
import { ChevronDownSmallIcon } from '@/ui/Icons/ChevronDownSmallIcon';
import { SearchIcon } from '@/ui/Icons/SearchIcon';

import { classNames } from '@/utils/classnames';
import { tradeRoute } from '@/utils/url';
import type { TokenBalance } from '@/utils/zerion/types';

import type { PortfolioRow } from './types';
import type { VirtualListItem } from './usePortfolioVirtualizer';
import { usePortfolioVirtualizer } from './usePortfolioVirtualizer';
import { formatTokenBalance } from './utils';

const PORTFOLIO_GRID_COLS = 'grid grid-cols-[200px_110px_90px_90px_80px_90px_90px_80px]';

export function TokenRowSkeleton() {
  return (
    <div className="flex items-center justify-between py-4 px-5 border-b border-token-alphalight-200">
      <div className="flex items-center gap-3">
        <AvatarSkeleton size={40} />
        <div className="flex flex-col gap-2">
          <TextSkeleton width={120} height={16} />
          <TextSkeleton width={80} height={12} />
        </div>
      </div>
      <div className="flex flex-col items-end gap-2">
        <TextSkeleton width={80} height={16} />
        <TextSkeleton width={60} height={12} />
      </div>
    </div>
  );
}

interface PortfolioTableProps {
  table: Table<PortfolioRow>;
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  hasTokens: boolean;
  hasFilteredTokens: boolean;
  selectedChain: number | 'all';
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  unpricedRows: PortfolioRow[];
  unpricedCount: number;
  showUnpricedTokens: boolean;
  onShowUnpricedTokensChange: (show: boolean) => void;
  smallBalanceCount: number;
  smallBalancesTotal: number;
  showSmallBalances: boolean;
  onShowSmallBalancesChange: (show: boolean) => void;
}

export function PortfolioTable({
  table,
  isLoading,
  error,
  onRetry,
  hasTokens,
  hasFilteredTokens,
  selectedChain,
  searchQuery,
  onSearchQueryChange,
  unpricedRows,
  unpricedCount,
  showUnpricedTokens,
  onShowUnpricedTokensChange,
  smallBalanceCount,
  smallBalancesTotal,
  showSmallBalances,
  onShowSmallBalancesChange,
}: PortfolioTableProps) {
  // Hook must be called unconditionally (Rules of Hooks) — before early returns
  const { listRef, totalSize, virtualRows, virtualizer, items } = usePortfolioVirtualizer({
    rows: table.getRowModel().rows,
    unpricedRows,
    unpricedCount,
    showUnpricedTokens,
    smallBalanceCount,
    showSmallBalances,
    enabled: !isLoading && !error && hasTokens && hasFilteredTokens,
  });

  if (isLoading) {
    return (
      <>
        {Array.from({ length: 5 }).map((_, i) => (
          <TokenRowSkeleton key={i} />
        ))}
      </>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <span
          className={fontStyles({
            variant: 'BODY_MD',
            class: 'text-token-gray-700 mb-4',
          })}
        >
          Failed to load portfolio
        </span>
        <button
          onClick={onRetry}
          className={buttons({ size: 'small', color: 'purple', shape: 'pill' })}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!hasTokens) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <span
          className={fontStyles({
            variant: 'BODY_MD',
            class: 'text-token-gray-700 text-center',
          })}
        >
          {selectedChain === 'all'
            ? 'No tokens found in your wallet'
            : `No tokens found on ${NETWORK_NAME_PER_CHAIN_ID[selectedChain]}`}
        </span>
      </div>
    );
  }

  return (
    <div className="min-w-max">
      <div
        className={classNames(
          'group/header',
          PORTFOLIO_GRID_COLS,
          'justify-between',
          'items-center py-4 pr-5 pl-2',
          'border-b border-token-gray-300',
        )}
      >
        <div className="flex items-center gap-2 pl-3">
          <SearchIcon className="w-4 h-4 text-token-gray-700" />
          <input
            type="text"
            placeholder="Search token"
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            className={classNames(
              fontStyles({ variant: 'LABEL_SM' }),
              'bg-transparent border-none outline-none text-token-gray-700 placeholder:text-token-gray-700',
              'w-[100px]',
            )}
          />
        </div>
        {table.getHeaderGroups().map((headerGroup) =>
          headerGroup.headers.slice(1).map((header) => (
            <div
              key={header.id}
              className={classNames(
                header.id === 'unrealizedReturn' ? 'whitespace-nowrap' : '',
                header.id === 'actions' ? 'flex justify-end text-right' : '',
              )}
            >
              {header.isPlaceholder
                ? null
                : flexRender(header.column.columnDef.header, header.getContext())}
            </div>
          )),
        )}
      </div>

      {!hasFilteredTokens && (
        <div className="flex flex-col items-center justify-center py-16">
          <span
            className={fontStyles({
              variant: 'BODY_MD',
              class: 'text-token-gray-700 text-center',
            })}
          >
            {`No tokens found matching "${searchQuery}"${selectedChain !== 'all' ? ` on ${NETWORK_NAME_PER_CHAIN_ID[selectedChain]}` : ''}`}
          </span>
          <button
            onClick={() => onSearchQueryChange('')}
            className={classNames(
              buttons({ size: 'small', color: 'alphaGray', shape: 'pill' }),
              'mt-4',
            )}
          >
            Clear search
          </button>
        </div>
      )}

      {hasFilteredTokens && (
        <div ref={listRef}>
          <div
            style={{
              height: `${totalSize}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {virtualRows.map((virtualRow) => {
              const item = items[virtualRow.index];
              return (
                <div
                  key={virtualRow.key}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
                  }}
                >
                  <VirtualItemRenderer
                    item={item}
                    isLastItem={virtualRow.index === items.length - 1}
                    unpricedCount={unpricedCount}
                    showUnpricedTokens={showUnpricedTokens}
                    onShowUnpricedTokensChange={onShowUnpricedTokensChange}
                    smallBalanceCount={smallBalanceCount}
                    smallBalancesTotal={smallBalancesTotal}
                    showSmallBalances={showSmallBalances}
                    onShowSmallBalancesChange={onShowSmallBalancesChange}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function VirtualItemRenderer({
  item,
  isLastItem,
  unpricedCount,
  showUnpricedTokens,
  onShowUnpricedTokensChange,
  smallBalanceCount,
  smallBalancesTotal,
  showSmallBalances,
  onShowSmallBalancesChange,
}: {
  item: VirtualListItem;
  isLastItem: boolean;
  unpricedCount: number;
  showUnpricedTokens: boolean;
  onShowUnpricedTokensChange: (show: boolean) => void;
  smallBalanceCount: number;
  smallBalancesTotal: number;
  showSmallBalances: boolean;
  onShowSmallBalancesChange: (show: boolean) => void;
}) {
  const { formatFiat } = useFiatDisplay();
  switch (item.type) {
    case 'table-row':
      return <TableRowRenderer row={item.row} isLastItem={isLastItem} />;
    case 'unpriced-toggle':
      return (
        <button
          type="button"
          aria-expanded={showUnpricedTokens}
          aria-controls="portfolio-unpriced-section"
          onClick={() => onShowUnpricedTokensChange(!showUnpricedTokens)}
          className={classNames(
            'flex items-center justify-center w-full h-11 px-5',
            'hover:bg-token-alphalight-100 transition-colors',
            'border-t border-token-gray-300 -mt-px',
            'cursor-pointer gap-0.5',
          )}
        >
          <span
            className={fontStyles({
              variant: 'LABEL_MD',
              class: 'text-token-gray-800',
            })}
          >
            {`Tokens without price (${unpricedCount})`}
          </span>
          <ChevronDownSmallIcon
            className={classNames(
              'w-4 h-4 text-token-gray-800 transition-transform duration-200',
              showUnpricedTokens ? 'rotate-180' : '',
            )}
          />
        </button>
      );
    case 'unpriced-row':
      return (
        <UnpricedRowRenderer
          unpricedRow={item.unpricedRow}
          isLastItem={isLastItem}
          hasTopBorder={item.isFirst}
        />
      );
    case 'small-balance-toggle':
      return (
        <button
          type="button"
          aria-expanded={showSmallBalances}
          aria-controls="portfolio-hidden-tokens-section"
          onClick={() => {
            onShowSmallBalancesChange(!showSmallBalances);
            if (showSmallBalances) {
              onShowUnpricedTokensChange(false);
            }
          }}
          className={classNames(
            'flex items-center justify-center w-full h-11 px-5',
            'hover:bg-token-alphalight-100 transition-colors',
            'border-t border-token-gray-300 -mt-px',
            'cursor-pointer gap-0.5',
          )}
        >
          <span
            className={fontStyles({
              variant: 'LABEL_MD',
              class: 'text-token-gray-800',
            })}
          >
            {`Hidden tokens (${smallBalanceCount}) ${formatFiat(smallBalancesTotal)}`}
          </span>
          <ChevronDownSmallIcon
            className={classNames(
              'w-4 h-4 text-token-gray-800 transition-transform duration-200',
              showSmallBalances ? 'rotate-180' : '',
            )}
          />
        </button>
      );
  }
}

function TableRowRenderer({ row, isLastItem }: { row: Row<PortfolioRow>; isLastItem: boolean }) {
  const { group, token } = row.original;
  const depth = row.depth;
  const isExpanded = row.getIsExpanded();
  const isGroupParent = depth === 0 && group != null;
  const isSubRow = depth > 0;
  const isSingleToken = depth === 0 && group == null;

  const needsStretchedTokenCell = isGroupParent || isSubRow;
  // The actions cell is `relative` so its button renders above the stretched row link
  // overlay instead of being nested inside the anchor.
  const cells = row.getVisibleCells().map((cell) => (
    <div
      key={cell.id}
      className={classNames(
        needsStretchedTokenCell && cell.column.id === 'tokenInfo' ? 'self-stretch' : '',
        cell.column.id === 'actions' ? 'relative' : '',
      )}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </div>
  ));
  if (isGroupParent) {
    return (
      <div
        className={classNames(!isExpanded && !isLastItem ? 'border-b border-token-gray-300' : '')}
      >
        <div
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          className={classNames(
            'group/row',
            PORTFOLIO_GRID_COLS,
            'justify-between',
            'items-center h-16 pr-5 pl-2',
            'hover:bg-token-alphalight-200 transition-colors',
            'cursor-pointer',
          )}
          onClick={() => row.toggleExpanded()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              row.toggleExpanded();
            }
          }}
        >
          {cells}
        </div>
      </div>
    );
  }

  if (isSubRow) {
    const parentRow = row.getParentRow();
    const isLastSubRow =
      parentRow != null && parentRow.subRows[parentRow.subRows.length - 1].id === row.id;

    return (
      <div
        className={classNames(
          'relative group/subrow',
          PORTFOLIO_GRID_COLS,
          'justify-between h-13',
          'items-center pr-5 pl-2',
          'border-t border-token-gray-300',
          isLastSubRow && !isLastItem ? 'border-b border-token-gray-300' : '',
          'bg-token-gray-200',
          'hover:bg-token-alphalight-300 transition-colors',
        )}
      >
        <RowTradeLink token={token} />
        {cells}
      </div>
    );
  }

  if (isSingleToken) {
    return (
      <div
        className={classNames(
          'relative group/row',
          PORTFOLIO_GRID_COLS,
          'justify-between',
          'items-center h-16 pr-5 pl-2',
          'hover:bg-token-alphalight-200 transition-colors',
          'border-token-gray-300',
          !isLastItem ? 'border-b' : '',
        )}
      >
        <RowTradeLink token={token} />
        {cells}
      </div>
    );
  }

  return null;
}

function UnpricedRowRenderer({
  unpricedRow,
  isLastItem,
  hasTopBorder,
}: {
  unpricedRow: PortfolioRow;
  isLastItem: boolean;
  hasTopBorder: boolean;
}) {
  return (
    <div
      className={classNames(
        'relative group/row',
        PORTFOLIO_GRID_COLS,
        'justify-between',
        'items-center h-16 pr-5 pl-2',
        'hover:bg-token-alphalight-200 transition-colors',
        hasTopBorder ? 'border-t border-t-token-alphalight-200' : '',
        'border-b-token-gray-300',
        !isLastItem ? 'border-b' : '',
      )}
    >
      <RowTradeLink token={unpricedRow.token} />
      <UnpricedTokenCells row={unpricedRow} />
    </div>
  );
}

/** Full-row overlay to the trade page. HyperCore tokens have no Matcha trade route. */
function RowTradeLink({ token }: { token: TokenBalance }) {
  if (token.chainId === HYPERCORE_CHAIN_ID) return null;
  return (
    <Link
      href={tradeRoute({ address: token.tokenAddress, chainId: token.chainId })}
      className="absolute inset-0"
      aria-label={`View ${token.symbol || token.name || token.tokenAddress}`}
    />
  );
}

function UnpricedTokenCells({ row }: { row: PortfolioRow }) {
  const { token } = row;
  const { formatFiat } = useFiatDisplay();
  const displayName =
    token.symbol ||
    token.name ||
    `${token.tokenAddress.slice(0, 6)}...${token.tokenAddress.slice(-4)}`;
  const formattedBalance = token.balanceFormatted ? parseFloat(token.balanceFormatted) : 0;
  const formattedBalanceStr = formatTokenBalance(formattedBalance);

  return (
    <>
      <div className="flex items-center gap-3 min-w-0 pl-6">
        <TokenAvatar chainId={token.chainId} address={token.tokenAddress} size={32} />
        <div className="min-w-0 flex-1">
          <p
            className={fontStyles({
              variant: 'BODY_MD',
              class: 'text-token-gray-1600 truncate',
            })}
          >
            {token.name || displayName}
          </p>
          <span className={fontStyles({ variant: 'BODY_MD', class: 'text-token-gray-800' })}>
            {displayName}
          </span>
        </div>
      </div>
      <div className="flex flex-col items-start">
        <span className={fontStyles({ variant: 'BODY_MD', class: 'text-token-gray-1600' })}>
          {token.balanceUsd != null ? formatFiat(token.balanceUsd) : '-'}
        </span>
        <span className={fontStyles({ variant: 'CAPTION', class: 'text-token-gray-800' })}>
          {formattedBalanceStr} {displayName}
        </span>
      </div>
      <div className="flex flex-col items-start">
        <span className={fontStyles({ variant: 'BODY_MD', class: 'text-token-gray-1600' })}>-</span>
      </div>
      <span className={fontStyles({ variant: 'BODY_MD', class: 'text-token-gray-600' })}>-</span>
      <span className={fontStyles({ variant: 'BODY_MD', class: 'text-token-gray-600' })}>-</span>
      <span className={fontStyles({ variant: 'BODY_MD', class: 'text-token-gray-600' })}>-</span>
      <span className={fontStyles({ variant: 'BODY_MD', class: 'text-token-gray-1600' })}>-</span>
      <div />
    </>
  );
}
